import { FlashNoticeVariant } from 'components/FlashNotice'
import { showFlashNotice } from 'components/FlashNotice/duck/actions'
import { setOneUser } from 'data/user/actions'
import { setOneUserGroup } from 'data/userGroups/actions'
import { adminApiGetUserGroup, apiGetUserGroup } from 'data/userGroups/api'
import { UserGroup } from 'data/userGroups/types'
import { callApi } from 'duck/operations'
import { isNil } from 'lodash'
import { all, put, select, takeEvery } from 'redux-saga/effects'
import { deleteDeal, markDealAsNew, setOneDeal, triggerDealReanalysisBegin } from 'scenes/Dashboard/data/deals/actions'
import { apiGetDeal, apiGetDealAsAdmin } from 'scenes/Dashboard/data/deals/api'
import { Deal } from 'scenes/Dashboard/data/deals/types'
import {
  apiGetAllFundingRoundsAsAdmin,
  apiGetAllFundingRoundsAsCurrentUser,
} from 'scenes/Dashboard/data/fundingRounds/api'
import { FundingRound } from 'scenes/Dashboard/data/fundingRounds/types'
import { setOneOrganization, updateOrganization } from 'scenes/Dashboard/data/organizations/actions'
import { apiGetOrganization, apiGetOrganizationAsAdmin } from 'scenes/Dashboard/data/organizations/api'
import { getOrganization } from 'scenes/Dashboard/data/organizations/selectors'
import { Organization } from 'scenes/Dashboard/data/organizations/types'
import { ActionType, getType } from 'typesafe-actions'

import { getApiCorporateHandlers } from '../../../../../entities-next/corporate'
import {
  fetchData,
  hideOrganizationDetails,
  showOrganizationDetails,
  showOrganizationDetailsAsAdmin,
  submitForm,
} from './actions'
import { getOrganizationDetailsModalState, isAdminOrganizationDetailsModalState } from './selectors'
import { OrganizationDetailsModalState } from './types'

export function* organizationDetailsModalSaga() {
  yield all([
    takeEvery(
      [getType(triggerDealReanalysisBegin), getType(deleteDeal.request), getType(markDealAsNew.success)],
      putHideOrganizationDetailsModal
    ),
    takeEvery([getType(showOrganizationDetails), getType(showOrganizationDetailsAsAdmin)], beginFetch),
    takeEvery(getType(updateOrganization.success), finishFormSubmit),
    takeEvery(getType(updateOrganization.failure), finishFormSubmit),
    takeEvery(getType(fetchData.request), fetchDataSaga),
  ])
}

function* finishFormSubmit() {
  yield put(submitForm.success())
  const { dealId, organizationId } = yield select(getOrganizationDetailsModalState)
  yield put(fetchData.request({ organizationId, dealId }))
}

function* beginFetch(action: ActionType<typeof showOrganizationDetails>) {
  yield put(fetchData.request(action.payload))
}

function* fetchDataSaga(action: ActionType<typeof fetchData.request>): any {
  const { dealId, organizationId } = action.payload
  try {
    if (dealId) {
      const deal: Deal = yield fetchDeal(dealId)
      const { organizationId: dealOrganizationId } = deal

      const organization: Organization = yield fetchOrganization(dealOrganizationId)

      const dealOwner = yield fetchDealOwner(deal)

      yield put(setOneDeal(deal))
      yield put(setOneOrganization(organization))

      if (dealOwner) {
        yield put(setOneUser(dealOwner))

        const userGroup = yield fetchUserGroup(dealOwner.userGroupId)
        yield put(setOneUserGroup(userGroup))
      }

      yield put(
        fetchData.success({
          dealId,
          organizationId: organization.id,
        })
      )
    } else if (organizationId) {
      try {
        const organization: Organization = yield fetchOrganization(organizationId)
        yield put(setOneOrganization(organization))
      } catch (e) {
        const organization: Organization = yield select(getOrganization, organizationId)
        yield put(
          showFlashNotice({
            message: `Organization "${organization.name}" has no content for this user.`,
            autoHide: false,
            variant: FlashNoticeVariant.warning,
          })
        )
        yield put(
          setOneOrganization({
            id: organization.id,
            name: organization.name,
            types: organization.types,
            domain: '',
            source: organization.source,
          })
        )
      }

      yield put(fetchData.success({ organizationId }))
    } else {
      throw new Error('Unavailable organization or deal')
    }
  } catch (e) {
    yield put(fetchData.failure(e))
  }
}

function* fetchDeal(dealId: number): any {
  const isAdminLoggedIn = yield select(isAdminOrganizationDetailsModalState)
  return yield callApi(isAdminLoggedIn ? apiGetDealAsAdmin : apiGetDeal, dealId)
}

function* fetchDealOwner(deal: Deal): any {
  if (isNil(deal.userId)) {
    return
  }
  const { apiGetCorporate } = getApiCorporateHandlers()
  return yield callApi(apiGetCorporate, deal.userId)
}

function* fetchUserGroup(userGroupId?: UserGroup['id']): any {
  if (!userGroupId) {
    return undefined
  }

  const isAdminLoggedIn = yield select(isAdminOrganizationDetailsModalState)

  return yield callApi(isAdminLoggedIn ? adminApiGetUserGroup : apiGetUserGroup, userGroupId)
}

function* fetchOrganization(organizationId: number): any {
  const isAdminLoggedIn = yield select(isAdminOrganizationDetailsModalState)

  const organizationData: Organization = yield callApi(
    isAdminLoggedIn ? apiGetOrganizationAsAdmin : apiGetOrganization,
    organizationId
  )

  const fundingRounds: FundingRound[] = yield callApi(
    isAdminLoggedIn ? apiGetAllFundingRoundsAsAdmin : apiGetAllFundingRoundsAsCurrentUser,
    organizationId
  )

  return { ...organizationData, fundingRounds }
}

function* putHideOrganizationDetailsModal() {
  const { show } = (yield select(getOrganizationDetailsModalState)) as OrganizationDetailsModalState
  if (show) {
    yield put(hideOrganizationDetails())
  }
}
